import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { Tooltip } from 'react-tooltip';
import { v4 as uuidv4 } from 'uuid';

import { ReactComponent as FullScreenIcon } from '@assets/svg/chart-icons/ic-fullScreen.svg';
import { ReactComponent as DownloadIcon } from '@assets/svg/ic-download.svg';

import LoaderGif from '@components/common/LoaderGif/LoaderGif';
import Tabs from '@components/uikit/Tabs/Tabs';

import { MODAL_NAMES } from '@constants/modalRenderers';

import { calcSensorHex } from '@helpers/helper';

import useTranslation from '@hooks/useTranslation';

import type { IInstallationPoint } from '@model/installationPoint';

import { useChartsTypesListQuery } from '@services/useChartsTypesListQuery';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { useModalActions } from '@store/useModalStore';

import { getChartTimesList } from '../../helpers/getChartTimesList';
import ChartAlertTooltip from '../ChartAlertTooltip/ChartAlertTooltip';
import ChartRmsView from '../ChartView/ChartRmsView';
import ChartTwfFftView from '../ChartView/ChartTwfFftView';
import SensorChartsSkeleton from './SensorChartsSkeleton';
import style from './style.module.scss';

interface IProps {
  point?: IInstallationPoint;
  regularAiId?: number;
  isModal?: boolean;
  isPointLoading?: boolean;
}

const ALERT_TOOLTIP_ID = 'alertLine';

const SensorCharts: React.FC<IProps> = ({ point, regularAiId, isModal = false, isPointLoading = false }) => {
  const { t } = useTranslation();
  const [timeValue, setTimeValue] = useState(point?.point_type === 'boolean' ? 1 : 14);
  const printRef = useRef<HTMLDivElement | null>(null);

  const { setActiveModal } = useModalActions();

  const { data: profileData } = useUserProfileQuery();
  const currentDateFormat = `${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`;

  const pointId = point?.id;
  const equipmentId = point?.equipment_id || point?.equipment?.id || '';

  const { data: chartTypeListData, isFetching: isFetchingChartTypeList } = useChartsTypesListQuery({
    pointId,
  });

  const isDataLoading = isPointLoading || isFetchingChartTypeList;
  const timesList = getChartTimesList(t);

  const handleOpenChartsModal = () => {
    setActiveModal({ name: MODAL_NAMES.chartsModal, options: { point } });
  };

  const handlePrint = useReactToPrint({
    documentTitle: `${t('Charts')} - ${point?.equipment?.name} - ${point?.name} - ${calcSensorHex(
      point?.sensor_id,
    )}`,
    contentRef: printRef,
    preserveAfterPrint: true,
  });

  if (!point?.id || !profileData?.userAccess?.manageSensorData) {
    return null;
  }

  return (
    <div className={style.chartsBlock}>
      {!isModal && (
        <div className={style.blockHead}>
          <div className={style.blockTitle}>{t('Sensor charts')}</div>
          {profileData?.userAccess?.showChartLink && (
            <Link
              to={`${APP_SELF_SERVICE_URL + 'chart/' + equipmentId}#sensors=${pointId}`}
              className={style.chartsBlockLink}
              target="_blank"
            >
              {t('View in the Self-Service Dashboard')}
            </Link>
          )}
        </div>
      )}
      <div className={style.blockContainer}>
        <div className={style.chartsHead}>
          <Tabs
            value={timeValue}
            tabsItems={timesList}
            label={t('Select dates')}
            type={'woHelperText'}
            onChange={setTimeValue}
          />
          {!isModal && (
            <div className={style.chartsActions}>
              <FullScreenIcon className={style.fullscreenIcon} onClick={handleOpenChartsModal} />
              {chartTypeListData && chartTypeListData?.length > 1 && (
                <DownloadIcon
                  className={style.downloadIcon}
                  data-tooltip-id="chart-info"
                  data-tooltip-content={t(`Download all charts in PDF`)}
                  onClick={() => handlePrint()}
                />
              )}
            </div>
          )}
        </div>
        <div ref={printRef}>
          {isDataLoading && <SensorChartsSkeleton />}
          {!isDataLoading &&
            chartTypeListData?.length &&
            chartTypeListData?.map((chartType) =>
              chartType.type === 'twf' || chartType.type === 'fft' ? (
                <ChartTwfFftView
                  key={uuidv4()}
                  point={point}
                  chartType={chartType}
                  dateTimeFormat={currentDateFormat}
                />
              ) : (
                <ChartRmsView
                  key={uuidv4()}
                  point={point}
                  chartType={chartType}
                  timeValue={timeValue}
                  dateTimeFormat={currentDateFormat}
                  alertTooltipId={ALERT_TOOLTIP_ID}
                />
              ),
            )}
        </div>
      </div>
      <Tooltip id="chart-info" />
      <ChartAlertTooltip
        alertTooltipId={ALERT_TOOLTIP_ID}
        pointInfo={{
          id: point.id,
          label: point.name,
          facilityId: point.equipment?.location?.facilityId,
          facilityOffset: point.equipment?.location?.facilityOffset,
        }}
        regularAiId={regularAiId}
      />
    </div>
  );
};

export default SensorCharts;
